export interface FAQItem {
  question: string
  answer: string | string[]
}

export interface TermsSubSection {
  heading: string
  items: string[]
}

export interface FAQSection {
  id: string
  title: string
  defaultExpanded?: boolean
  items?: FAQItem[]
  content?: string[]
  termsSubSections?: TermsSubSection[]
  helperContent?: {
    text: string
    link: string
  }
}

export const faqData: FAQSection[] = [
  {
    id: 'eligibility',
    title: 'Contest eligibility',
    defaultExpanded: false,
    content: [
      'Individual aged 18 years or above and a resident of India.',
      'Valid ID proof (Aadhaar, Driving License, Voter ID, or Passport) and PAN Card.',
      'Must have an active ACKO car policy during the Car Drop period and on the winner announcement date.',
      'Participation is valid only after opting in the Survey and completing the "Wishlist a Car" activity.',
    ],
    helperContent: {
      text: 'Please read the Terms & Conditions for complete information before participating. For queries, contact',
      link: 'cardrop@ackodrive.com',
    },
  },
  {
    id: 'terms',
    title: 'Terms and conditions',
    defaultExpanded: false,
    termsSubSections: [
      {
        heading: 'Organizer:',
        items: [
          'The ACKO Drive Car Drop ("Car Drop") is organized by ACKO Technology and Services Pvt. Ltd. ("ACKO").',
        ],
      },
      {
        heading: 'Participation:',
        items: [
          'Open only to existing ACKO Car Policy holders with an active policy during the Car Drop period and on the winner announcement date.',
          'To participate, log in to the ACKO App or ACKO Drive, click "Opt In," and complete the "Wishlist a Car" activity.',
          'Only one entry per user/account per month is allowed.',
        ],
      },
      {
        heading: 'Winner Selection:',
        items: [
          'Winners will be selected via a random computerized draw from eligible entries.',
          'Completing the activity does not guarantee a win.',
          'The car make, model, and color will be decided solely by ACKO.',
        ],
      },
      {
        heading: 'Prize Details:',
        items: [
          'The prize car is non-transferable, non-exchangeable, and cannot be redeemed for cash.',
          "ACKO will bear the car's full on-road price, including registration and road tax.",
          "The car will be registered in the winner's name only.",
        ],
      },
      {
        heading: 'Taxes & Payments:',
        items: [
          'Applicable taxes (including TDS under Section 194B) must be paid by the winner before claiming the prize.',
          'Winners must confirm acceptance within 48 hours and pay ₹50,000 as a non-refundable advance towards TDS.',
          'The remaining tax must be paid before car delivery.',
        ],
      },
      {
        heading: 'Winner Announcement:',
        items: [
          'Winners will be contacted via call, email, or WhatsApp and listed on the ACKO Drive platform.',
          'Failure to respond within 2 days may lead to forfeiture of the prize.',
        ],
      },
      {
        heading: 'Publicity & Consent:',
        items: [
          'By participating, you consent to ACKO using your name, image, and statements for promotional purposes across digital and social media, without additional compensation.',
        ],
      },
      {
        heading: 'General Terms:',
        items: [
          'ACKO may modify, suspend, or cancel the Car Drop at any time.',
          'Winners of one Car Drop are not eligible for future Car Drops.',
          'ACKO is not liable for delays, losses, or technical issues affecting participation or prize delivery.',
          'All decisions of ACKO are final and binding.',
          'Disputes are subject to Indian law and jurisdiction of courts in Bangalore.',
        ],
      },
    ],
    helperContent: {
      text: 'The above synopsis is provided for information and reference only. Please read the Terms & Conditions for complete information before participating. For queries, contact',
      link: 'cardrop@ackodrive.com',
    },
  },
  {
    id: 'faq',
    title: 'Frequently asked questions',
    defaultExpanded: false,
    items: [
      {
        question: 'What is the ACKO Drive Car Drop?',
        answer:
          'The ACKO Drive Car Drop is an exclusive program for ACKO Car Policy holders, giving you a chance to win a brand-new car and exciting rewards by completing a simple "Wishlist 3 Cars" survey on ACKO Drive website.',
      },
      {
        question: 'Who can participate in the Car Drop?',
        answer:
          'Only individuals aged 18 or above who are current ACKO Car Policy holders with an active policy during the Car Drop Period and on the winner announcement date. Only one entry per individual is allowed in a Car Drop.',
      },
      {
        question: 'How do I participate?',
        answer: [
          'Log in / Sign-Up on the Acko Drive Website',
          'Go to the ACKO Drive Car Drop page.',
          'Click on "Start wishlisting a car" for the Car Drop.',
          'Wishlist 3 cars & simply complete your participation.',
        ],
      },
      {
        question: 'Do I need to make any payment to enter?',
        answer:
          'No. Participation in the Car Drop is completely free for eligible users.',
      },
      {
        question: 'Is there a deadline to participate?',
        answer:
          'Yes, the Car Drop is for a limited period. All entries must be received during the participation window as mentioned on the Car Drop page.',
      },
      {
        question: 'How are winners selected?',
        answer:
          'Winners are chosen through a random computerized draw from all valid and eligible entries who have completed the Wishlist 3 Cars activity.',
      },
      {
        question: 'What can I win?',
        answer:
          "You stand a chance to win a brand-new car selected by ACKO Drive. The car model, color, and variant are decided at ACKO's sole discretion. The prize includes the full on-road price of the car, which covers registration, road tax, and related mandatory charges. Any additional costs, such as optional accessories, extended warranties, or other taxes and duties, will be the responsibility of the winner.",
      },
      {
        question: 'Do I need to provide ID or documents to claim the prize?',
        answer:
          "Yes. Winners will need to provide valid identification proof, PAN and any necessary documents required for vehicle registration and prize verification by us, dealer, OEM and/or any government authority / body. This ensures the prize can be legally transferred to the winner and claimed in the winner's name.",
      },
      {
        question: 'Will the car be registered in my name?',
        answer:
          "Yes, the car will be registered only in the winner's name, as per RTO regulations.",
      },
      {
        question: 'Do I have to pay any taxes if I win?',
        answer:
          'Yes. As per Indian tax laws, the winner must pay applicable taxes, including TDS under Section 194B, before claiming the prize. ACKO will cover the full on-road cost of the car, including registration and road tax.',
      },
      {
        question: 'What is the ₹50,000 payment mentioned in the T&Cs?',
        answer:
          "If you're declared a winner, you'll need to pay ₹50,000 as an advance towards TDS within 48 hours of confirmation and sign a winners agreement. This amount is non-refundable and adjusted against the total tax payable.",
      },
      {
        question: 'How and when will winners be announced?',
        answer:
          "Winners will be contacted by ACKO via call, email, and WhatsApp. Their names will also be displayed under the Winner's Tab on ACKO Drive.",
      },
      {
        question: "What happens if I don't respond after being selected?",
        answer:
          "If a winner doesn't confirm acceptance or pay the advance tax within the specified time, the prize will be forfeited, and another winner may be selected.",
      },
      {
        question: 'Can I transfer my prize to someone else?',
        answer:
          'No. The prize is personal, non-transferable, non-exchangeable, non-returnable and cannot be redeemed for cash.',
      },
      {
        question: 'How long will it take to receive the car?',
        answer:
          'Delivery timelines depend on OEM and RTO processing, but typically take around 2–3 months after all formalities are completed.',
      },
      {
        question: 'Can I choose the car model or variant?',
        answer:
          'No. The car model, color, and variant will be decided solely by ACKO and may differ from the one you selected in your wishlist.',
      },
      {
        question: 'Will I win the car I wishlisted in the "Wishlist a Car" survey?',
        answer:
          'No. Wishlisting a car is purely a survey exercise to understand user preferences. The final prize will be determined solely by ACKO and may differ from the car you selected in your wishlist. Please refer to the Prize tab for more details.',
      },
      {
        question: 'How often does the Car Drop happen?',
        answer:
          "The Car Drop is a recurring campaign. However, prizes and rounds are subject to change from time to time at ACKO's discretion. Keep an eye on the Car Drop section for the latest details on upcoming drops and prizes.",
      },
      {
        question: 'Can a winner participate in future car drops?',
        answer:
          'No. Winners from one Car Drop cannot participate in future rounds. This ensures that more participants have a fair chance to win.',
      },
      {
        question: 'Can I participate on behalf of someone else?',
        answer:
          'No. Participation is strictly individual. Each entry must be submitted by the person who holds the Acko account to claim the prize, and prizes can only be awarded to the registered user.',
      },
      {
        question: 'Who do I contact for queries?',
        answer:
          'For more details, please read the Terms and Conditions carefully. For any questions or support, please write to us at cardrop@ackodrive.com.',
      },
      {
        question: 'Can I choose the delivery location for the car?',
        answer:
          'Delivery location will depend on your state of residence or address as provided in the submitted documents, and is subject to applicable laws and regulations.',
      },
    ],
  },
]

